/**
 * DirectoryTab: who is who, for every internal seat, as a roster.
 *
 * ONE table, not a card grid:
 *   - avatar through the private-contact lock chip, sortable by
 *     clicking a header (name / role / unit / since) and filterable by a
 *     search box and a unit/role filter pill above it -- the same
 *     FilterPill + bottom-sheet idiom the client roster uses (Clients.tsx).
 *     Sorted by unit shows the unit's quiet group header (with its county
 *     line from GET /units/coverage) and drops the now-redundant per-row
 *     Unit cell; any other sort shows the cell and drops the headers.
 *   - the roster area takes the full content width -- Coming up is a
 *     compact chip band between the filters and the table, not a side
 *     column, so nothing here narrows the table's own available width.
 *     See ComingUpBand.tsx.
 *   - below 900px the table becomes a two-line list; same filters, same
 *     drawer.
 *
 * Clicking a row opens the person's own drawer (PersonDetailDrawer,
 * reusing the People page's own drawer chrome and directory-fields
 * editor): editable for a manage_staff_profiles seat, read-only for
 * everyone else. "Download roster" exports the table's CURRENT filter
 * (the roster_ids on screen), not the whole roster, and the filename says
 * so when filtered.
 */

import { useState, type ReactNode } from 'react';
import { Check, Download } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { canManageStaffProfiles, canViewStaffPrivateContact } from '@/lib/capabilities';
import { downloadDocx } from '@/lib/downloadDocx';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  DEFAULT_ROSTER_SORT,
  EMPTY_ROSTER_FILTERS,
  filterRoster,
  nextRosterSort,
  rosterDocxUrl,
  rosterFilename,
  rosterRoleOptions,
  rosterUnitOptions,
  sortRoster,
  todayInOperatingZone,
  useTeamDirectory,
  type RosterFilters,
  type RosterSort,
  type TeamDirectoryPerson,
} from '@/lib/teamDirectory';
import { useUnitCoverage } from '@/lib/unitCoverage';
import { BottomSheet } from '@/components/primitives/BottomSheet';
import { Button } from '@/components/primitives/Button';
import { FilterPill } from '@/components/primitives/FilterPill';
import { SearchField } from '@/components/primitives/SearchField';
import { SkeletonRow } from '@/components/Skeleton';
import { ErrorBanner } from '@/components/ErrorBanner';
import { RosterTable } from './RosterTable';
import { ComingUpBand } from './ComingUpBand';
import { PersonDetailDrawer } from './PersonDetailDrawer';
import { contactSentence } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

/** Below this width the table becomes a two-line list (spec: "below 900px"). */
const NARROW_BREAKPOINT_PX = 899;

interface DirectoryPickerOption {
  value: string;
  label: string;
}

/**
 * PickerSheet -- single-select bottom sheet behind a FilterPill trigger,
 * the same idiom the client roster's filters use (src/pages/Clients.tsx
 * PickerSheet): big touch rows, readable words, Escape + backdrop close
 * via the BottomSheet primitive.
 */
function PickerSheet({
  title,
  isOpen,
  onClose,
  options,
  value,
  onSelect,
  testId,
}: {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  options: ReadonlyArray<DirectoryPickerOption>;
  value: string;
  onSelect: (value: string) => void;
  testId: string;
}): ReactNode {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={title}>
      <div className="px-5 pt-2 pb-5" role="listbox" aria-label={title} data-testid={testId}>
        {options.map((o) => {
          const on = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={on}
              onClick={() => {
                onSelect(o.value);
                onClose();
              }}
              className={`flex w-full items-center gap-3 border-b border-sage-800/[8%] py-3 text-left text-sm last:border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-md ${
                on ? 'font-semibold text-ink' : 'font-medium text-ink'
              }`}
            >
              <span
                className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full ${
                  on ? 'bg-sage-800 text-cream' : 'border-[1.5px] border-sage-300'
                }`}
              >
                {on && <Check size={11} strokeWidth={2.4} aria-hidden="true" />}
              </span>
              <span className="flex-1">{o.label}</span>
            </button>
          );
        })}
      </div>
    </BottomSheet>
  );
}

type DirectoryPickerKey = 'unit' | 'role';

export interface DirectoryTabProps {
  tenantSlug: string;
}

export function DirectoryTab({ tenantSlug }: DirectoryTabProps): ReactNode {
  const { whoami } = useTenant();
  const contact = useSupportContact();
  const query = useTeamDirectory(tenantSlug);
  const coverage = useUnitCoverage(tenantSlug);
  const countiesByUnit = new Map(coverage.units.map((u) => [u.unit_name, u.counties]));
  const isNarrow = useIsMobile(NARROW_BREAKPOINT_PX);
  const today = todayInOperatingZone();

  const [filters, setFilters] = useState<RosterFilters>(EMPTY_ROSTER_FILTERS);
  const [sort, setSort] = useState<RosterSort>(DEFAULT_ROSTER_SORT);
  const [openRosterId, setOpenRosterId] = useState<string | null>(null);
  const [rosterStatus, setRosterStatus] = useState<'idle' | 'preparing' | 'error'>('idle');
  const [openPicker, setOpenPicker] = useState<DirectoryPickerKey | null>(null);

  const people = query.data?.people ?? [];
  const privateContactVisible = query.data?.private_contact_visible === true;
  const canEdit = canManageStaffProfiles(whoami);
  const canDownloadRoster = canViewStaffPrivateContact(whoami) || canEdit;

  const filtered = filterRoster(people, filters);
  const shown = sortRoster(filtered, sort);
  const filtersActive = filters.search.trim().length > 0 || filters.unit !== null || filters.role !== null;

  const unitOptions = rosterUnitOptions(people);
  const roleOptions = rosterRoleOptions(people);
  const unitPickerOptions: DirectoryPickerOption[] = [
    { value: '', label: 'Every unit' },
    ...unitOptions.map((u) => ({ value: u, label: u })),
  ];
  const rolePickerOptions: DirectoryPickerOption[] = [
    { value: '', label: 'Every role' },
    ...roleOptions.map((r) => ({ value: r, label: r })),
  ];
  const unitPillLabel = filters.unit === null ? 'Unit' : `Unit: ${filters.unit}`;
  const rolePillLabel = filters.role === null ? 'Role' : `Role: ${filters.role}`;
  // Re-derived from the full list so a filter change never closes an open drawer.
  const openPerson = openRosterId === null ? null : (people.find((p) => p.roster_id === openRosterId) ?? null);

  async function downloadRoster(): Promise<void> {
    setRosterStatus('preparing');
    try {
      const ids = filtersActive ? shown.map((p) => p.roster_id) : undefined;
      await downloadDocx(rosterDocxUrl(tenantSlug, ids), rosterFilename(today, { filtered: filtersActive }));
      setRosterStatus('idle');
    } catch {
      setRosterStatus('error');
    }
  }

  return (
    <div data-testid="team-directory" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        {query.data ? (
          <p className="text-sm text-ink-soft" data-testid="team-count">
            {shown.length} of {people.length} people
          </p>
        ) : (
          <span />
        )}
        {canDownloadRoster && query.data && (
          <span className="inline-flex flex-col items-start gap-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              icon={<Download size={14} />}
              loading={rosterStatus === 'preparing'}
              disabled={shown.length === 0}
              onClick={() => void downloadRoster()}
              data-testid="team-roster-download"
            >
              {rosterStatus === 'preparing' ? 'Preparing roster' : 'Download roster'}
            </Button>
            {rosterStatus === 'error' && (
              <span role="alert" className="text-xs text-warm-700" data-testid="team-roster-download-error">
                {contactSentence("The roster didn't download. Try again, and if it happens again, tell {contact_system}.", contact)}
              </span>
            )}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <SearchField
          value={filters.search}
          onChange={(next) => setFilters((f) => ({ ...f, search: next }))}
          aria-label="Find a person"
          placeholder="Find a person"
          testId="team-search"
          className="w-64"
        />
        <FilterPill
          label={unitPillLabel}
          active={filters.unit !== null}
          onClick={() => setOpenPicker('unit')}
          data-testid="team-unit-filter"
        />
        <FilterPill
          label={rolePillLabel}
          active={filters.role !== null}
          onClick={() => setOpenPicker('role')}
          data-testid="team-role-filter"
        />
      </div>

      {query.data && <ComingUpBand people={people} today={today} />}

      {query.isLoading ? (
        <div className="space-y-2.5"><SkeletonRow /><SkeletonRow /><SkeletonRow /></div>
      ) : query.isError ? (
        <ErrorBanner error={query.error} onRetry={query.refetch} what="the directory" />
      ) : shown.length === 0 ? (
        <p className="py-6 text-sm text-ink-soft" data-testid="team-empty">
          {filtersActive ? 'Clear a filter to widen the list.' : "Add people on the People page and you'll see them here."}
        </p>
      ) : (
        <RosterTable
          tenantSlug={tenantSlug}
          people={shown}
          sort={sort}
          onSort={(field) => setSort((s) => nextRosterSort(s, field))}
          countiesByUnit={countiesByUnit}
          onRowClick={(p: TeamDirectoryPerson) => setOpenRosterId(p.roster_id)}
          today={today}
          isNarrow={isNarrow}
        />
      )}

      {openPerson && (
        <PersonDetailDrawer
          tenantSlug={tenantSlug}
          person={openPerson}
          today={today}
          privateContactVisible={privateContactVisible}
          canEdit={canEdit}
          onClose={() => setOpenRosterId(null)}
        />
      )}

      <PickerSheet
        title="Unit"
        isOpen={openPicker === 'unit'}
        onClose={() => setOpenPicker(null)}
        options={unitPickerOptions}
        value={filters.unit ?? ''}
        onSelect={(v) => setFilters((f) => ({ ...f, unit: v === '' ? null : v }))}
        testId="team-unit-filter-sheet"
      />
      <PickerSheet
        title="Role"
        isOpen={openPicker === 'role'}
        onClose={() => setOpenPicker(null)}
        options={rolePickerOptions}
        value={filters.role ?? ''}
        onSelect={(v) => setFilters((f) => ({ ...f, role: v === '' ? null : v }))}
        testId="team-role-filter-sheet"
      />
    </div>
  );
}
